import * as React from "react";
import get from "lodash/get";

import "./BirthdayInput.css";
import * as actions from "../../actions";
import { isEditMode } from "../../utils/sendClickToMerchant";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import { flattenObject } from "../../utils/flattenObject";
import { generateErrorId } from "../../utils/string";

export type CollectBirthYear = "dontAsk" | "optional" | "required";

export interface BirthdayValue {
  day?: number;
  month?: number;
  year?: number;
}

export interface BirthdayInputProps extends ComponentBaseProps {
  clearWidgetFormError: typeof actions.clearWidgetFormError;
  formErrors: {
    [key: string]: { [index: string]: any };
  };
  configName: string;
  // "Collect birth year": whether the Year dropdown is shown, and (when
  // "required") whether the year is part of a complete date.
  collectBirthYear?: CollectBirthYear;
  // A three-select control has no single placeholder, so the merchant sets a
  // label rendered above the Month/Day/Year row (ENH-344).
  fieldLabel?: string;
  monthPlaceholder?: string;
  dayPlaceholder?: string;
  yearPlaceholder?: string;
  style?: React.CSSProperties;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const YEARS_BACK = 120;

/**
 * The Birthday "known field" (ENH-344). Renders Month + Day dropdowns
 * always, and a Year dropdown unless "Collect birth year" is "dontAsk". Stores
 * the value as a standard `{ day, month, year? }` object (the same shape as
 * `customer.birthday`) so fbt-account can promote it to the standard customer
 * field. Only the selected parts are set, so a partial date can be validated by
 * the isCompleteDate validator.
 */
export class BirthdayInput extends ComponentBase<BirthdayInputProps> {
  private buildValue(next: BirthdayValue): BirthdayValue | undefined {
    const value: BirthdayValue = {};
    if (next.month != null) {
      value.month = next.month;
    }
    if (next.day != null) {
      value.day = next.day;
    }
    if (next.year != null) {
      value.year = next.year;
    }

    return value.month == null && value.day == null && value.year == null
      ? undefined
      : value;
  }

  private current(): BirthdayValue {
    return (get(this.props.data, this.props.name) as BirthdayValue) || {};
  }

  private emit(part: "month" | "day" | "year", raw: string) {
    const parsed = raw === "" ? undefined : Number(raw);
    const next = { ...this.current(), [part]: parsed };
    this.onFormChange(this.buildValue(next));

    const formErrors = flattenObject(get(this.props, "formErrors", {}));
    if (formErrors[this.props.name]) {
      // Re-validate as the user corrects an already-flagged field.
      this.validateField(true, next);
    }
  }

  private onMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    this.emit("month", event.target.value);

  private onDayChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    this.emit("day", event.target.value);

  private onYearChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    this.emit("year", event.target.value);

  render() {
    const {
      style,
      name,
      collectBirthYear = "optional",
      fieldLabel,
      monthPlaceholder = "Month",
      dayPlaceholder = "Day",
      yearPlaceholder = "Year",
    } = this.props;

    const value = this.current();
    const isPreviewMode = !isEditMode();
    const formErrors = flattenObject(get(this.props, "formErrors", {}));
    const hasError = formErrors[name];
    const showYear = collectBirthYear !== "dontAsk";

    const currentYear = new Date().getFullYear();
    const years = Array.from(
      { length: YEARS_BACK + 1 },
      (_, i) => currentYear - i
    );

    // Ties the visible field label to the group for assistive tech.
    const labelId = `${this.props.configName}-label`;

    // The error lives on the wrapper, which never receives focus — screen
    // readers only announce a description attached to the focused control, so
    // each select points at it.
    const selectAria = hasError
      ? { "aria-describedby": generateErrorId(name), "aria-invalid": true }
      : {};

    const selectClassName = `BirthdayInput-select ${
      hasError ? "input-error" : ""
    } ${isPreviewMode ? "BirthdayInput-select-preview" : ""}`;

    return (
      <div
        id={this.props.configName}
        className="BirthdayInput"
        // The configured height governs the whole control, exactly as it does
        // for every other component — so positioning/alignment in the builder
        // stays predictable and the control can never overflow onto the field
        // below. The select row flexes into whatever is left under the label.
        style={style}
        // role=group + aria-labelledby is used instead of fieldset/legend: the
        // three selects are one logical field, and a fieldset would change the
        // box model this component's flex layout depends on.
        role="group"
        aria-label={
          fieldLabel ? undefined : get(this.props, "label", "")
        }
        {...(fieldLabel ? { "aria-labelledby": labelId } : {})}
        ref={(component) => (this.component = component)}
      >
        {fieldLabel && (
          <label id={labelId} className="BirthdayInput-label">
            {fieldLabel}
          </label>
        )}
        <div className="BirthdayInput-selects">
          <select
            className={selectClassName}
            aria-label={monthPlaceholder}
            {...selectAria}
            value={value.month != null ? String(value.month) : ""}
            onChange={this.onMonthChange}
          >
            <option value="">{monthPlaceholder}</option>
            {MONTHS.map((label, index) => (
              <option key={label} value={index + 1}>
                {label}
              </option>
            ))}
          </select>

          <select
            className={selectClassName}
            aria-label={dayPlaceholder}
            {...selectAria}
            value={value.day != null ? String(value.day) : ""}
            onChange={this.onDayChange}
          >
            <option value="">{dayPlaceholder}</option>
            {DAYS.map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>

          {showYear && (
            <select
              className={selectClassName}
              aria-label={yearPlaceholder}
            {...selectAria}
              value={value.year != null ? String(value.year) : ""}
              onChange={this.onYearChange}
            >
              <option value="">{yearPlaceholder}</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    );
  }
}
